import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Container, Typography, List, ListItem, ListItemText, Box, Button, TextField, MenuItem, Stack, Switch, Chip, InputAdornment } from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon, Close as CloseIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import systemInventoryService, { SystemInventory } from '../../services/systemInventoryService';
import { usePageTitle } from '../../hooks/usePageTitle';

const SystemInventoryPage: React.FC = () => {
  usePageTitle('Systems Inventory');
  
  const navigate = useNavigate();
  const [items, setItems] = useState<SystemInventory[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [form, setForm] = useState<Partial<SystemInventory>>({});
  const [q, setQ] = useState<string>('');
  const firstFieldRef = useRef<HTMLInputElement | null>(null);

  const load = async (query?: string) => {
    setLoading(true);
    setError('');
    try {
      const data = await systemInventoryService.list(query);
      setItems(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to load systems');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (editingId && firstFieldRef.current) firstFieldRef.current.focus(); }, [editingId]);

  const startEdit = (it: SystemInventory) => { setEditingId(it._id); setForm({ ...it }); };
  const startCreate = () => { setEditingId('new'); setForm({ name: '', systemType: 'Other', status: 'Active', order: (items?.length || 0) + 1, isActive: true }); };
  const cancelEdit = () => { setEditingId(null); setForm({}); };

  const save = async () => {
    try {
      if (editingId === 'new') {
        const created = await systemInventoryService.create({
          name: form.name || '', acronym: form.acronym,
          systemType: (form.systemType as any) || 'Other',
          description: form.description, owner: form.owner,
          status: form.status || 'Active',
          order: form.order, isActive: form.isActive,
        });
        setItems((prev) => [created, ...prev]);
      } else if (editingId) {
        const updated = await systemInventoryService.update(editingId, form);
        setItems((prev) => prev.map((p) => (p._id === updated._id ? updated : p)));
      }
      cancelEdit();
    } catch (e: any) { setError(e?.response?.data?.error || 'Save failed'); }
  };

  const remove = async (id: string) => {
    try {
      await systemInventoryService.remove(id);
      setItems((prev) => prev.filter((p) => p._id !== id));
      if (editingId === id) cancelEdit();
    } catch (e: any) { setError(e?.response?.data?.error || 'Delete failed'); }
  };

  const filtered = useMemo(() => {
    if (!Array.isArray(items)) return [];
    if (!q) return items;
    const s = q.toLowerCase();
    return items.filter((i) => [i.name, i.acronym, i.systemType, i.description, i.owner].filter(Boolean).some((v) => String(v).toLowerCase().includes(s)));
  }, [items, q]);

  const renderForm = (isNew: boolean) => (
    <Stack spacing={1} sx={{ width: '100%' }}>
      <Stack direction="row" spacing={1}>
        <TextField inputRef={firstFieldRef} label="Name" fullWidth value={form.name || ''} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
        <TextField label="Acronym" sx={{ width: 160 }} value={form.acronym || ''} onChange={(e) => setForm((f) => ({ ...f, acronym: e.target.value }))} />
      </Stack>
      <Stack direction="row" spacing={1}>
        <TextField select label="System Type" sx={{ width: 200 }} value={form.systemType || 'Other'} onChange={(e) => setForm((f) => ({ ...f, systemType: e.target.value as any }))}>
          {['Application', 'Database', 'Platform', 'Infrastructure', 'Service', 'Other'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
        <TextField select label="Status" sx={{ width: 150 }} value={form.status || 'Active'} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as any }))}>
          {['Active', 'Inactive', 'Deprecated', 'Planned'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        <TextField label="Owner" sx={{ flex: 1 }} value={form.owner || ''} onChange={(e) => setForm((f) => ({ ...f, owner: e.target.value }))} />
        <TextField label="Order" type="number" sx={{ width: 100 }} value={form.order ?? ''} onChange={(e) => setForm((f) => ({ ...f, order: Number(e.target.value) }))} />
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography component="span">Active</Typography>
          <Switch checked={!!form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} />
        </Stack>
      </Stack>
      <TextField label="Description" multiline minRows={2} value={form.description || ''} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
      <Stack direction="row" spacing={1}>
        <Button onClick={save} startIcon={<SaveIcon />} variant="contained">Save</Button>
        <Button onClick={cancelEdit} startIcon={<CloseIcon />} color="inherit">Cancel</Button>
        {!isNew && <Button onClick={() => remove(form._id!)} startIcon={<DeleteIcon />} color="error">Delete</Button>}
      </Stack>
    </Stack>
  );

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button onClick={() => navigate('/dhs')} startIcon={<ArrowBackIcon />} sx={{ mr: 2 }}>Back</Button>
        <Box sx={{ flex: 1 }} />
        <TextField size="small" placeholder="Search systems" value={q} onChange={(e) => setQ(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
        <Button onClick={startCreate} startIcon={<AddIcon />} sx={{ ml: 2 }}>Add</Button>
      </Box>
      <Typography variant="h4" component="h1" sx={{ color: 'primary.main', fontWeight: 700, mb: 2 }}>Systems Inventory</Typography>
      <Typography variant="body1" sx={{ mb: 3 }}>Applications, databases, platforms, and infrastructure systems used by DHS.</Typography>
      {error && <Typography role="alert" sx={{ color: 'error.main', mb: 2 }}>{error}</Typography>}
      <List>
        {editingId === 'new' && <ListItem divider sx={{ bgcolor: 'background.default' }}>{renderForm(true)}</ListItem>}
        {filtered.map((it) => (
          <ListItem key={it._id} divider>
            {editingId === it._id ? renderForm(false) : (
              <Box role="button" tabIndex={0} onClick={() => startEdit(it)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); startEdit(it); }}} style={{ width: '100%', outline: 'none' }}>
                <Stack spacing={0.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ListItemText primaryTypographyProps={{ sx: { fontWeight: 700 } }} primary={it.name} />
                    {it.acronym && <Chip size="small" label={it.acronym} />}
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip size="small" color="primary" label={it.systemType} />
                    <Chip size="small" label={it.status} color={it.status === 'Active' ? 'success' : it.status === 'Deprecated' ? 'error' : 'default'} />
                    {it.owner && <Chip size="small" label={it.owner} />}
                    <Chip size="small" label={it.isActive ? 'Active' : 'Inactive'} color={it.isActive ? 'success' : 'default'} />
                  </Stack>
                  {it.description && <Typography variant="body2" sx={{ color: 'text.secondary' }}>{it.description}</Typography>}
                </Stack>
              </Box>
            )}
          </ListItem>
        ))}
        {loading && <ListItem><ListItemText primary="Loading..." /></ListItem>}
        {!loading && filtered.length === 0 && <ListItem><ListItemText primary="No systems found" /></ListItem>}
      </List>
    </Container>
  );
};

export default SystemInventoryPage;
